import { Box, Button, Stack, Typography } from '@mui/material';
import { Add as AddIcon, CreditCardOff as NoCardIcon } from '@mui/icons-material';

import type { PatientPaymentMethod } from '../types';

import { PatientPaymentMethodCard } from './PatientPaymentMethodCard';

export interface PatientPaymentMethodListProps {
  paymentMethods: PatientPaymentMethod[];
  onSetDefault: (paymentMethodId: string) => Promise<void>;
  onDelete: (paymentMethodId: string) => Promise<void>;
  onAddClick: () => void;
  isSettingDefault?: boolean;
  isDeleting?: boolean;
}

/**
 * List of patient payment methods with empty state
 */
export function PatientPaymentMethodList({
  paymentMethods,
  onSetDefault,
  onDelete,
  onAddClick,
  isSettingDefault = false,
  isDeleting = false,
}: PatientPaymentMethodListProps) {
  if (paymentMethods.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          px: 2,
          textAlign: 'center',
        }}
      >
        <NoCardIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No payment methods on file.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddClick}
          sx={{
            textTransform: 'none',
            borderRadius: '0.5rem',
          }}
        >
          Add Payment Method
        </Button>
      </Box>
    );
  }

  // Sort to show default first
  const sortedMethods = [...paymentMethods].sort((a, b) => {
    if (a.is_default && !b.is_default) return -1;
    if (!a.is_default && b.is_default) return 1;
    return 0;
  });

  return (
    <Stack spacing={1}>
      {sortedMethods.map(method => (
        <PatientPaymentMethodCard
          key={method.id}
          paymentMethod={method}
          onSetDefault={onSetDefault}
          onDelete={onDelete}
          isSettingDefault={isSettingDefault}
          isDeleting={isDeleting}
        />
      ))}
    </Stack>
  );
}

export default PatientPaymentMethodList;
